import React, { useEffect } from 'react';

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  mobile = true
}) => {
  useEffect(() => {
    if (isOpen) {
      // Add modal-open class for iOS stability
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  const handleBackdropClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  };

  // Mobile-first modal design with dynamic height
  if (mobile && typeof window !== 'undefined' && window.innerWidth < 768) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end" style={{ touchAction: 'none' }}>
        <div className="bg-white rounded-t-3xl w-full flex flex-col" style={{
          minHeight: '50vh',
          maxHeight: '90vh',
          height: 'auto',
          touchAction: 'pan-y',
          position: 'relative'
        }}>
          {/* Header with ref for dynamic calculation */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Dynamic content container with proper scrolling */}
          <div 
            className="flex-1 overflow-y-auto p-4 modal-content-scroll" 
            style={{
              paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
              overscrollBehaviorX: 'none'
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Desktop modal
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className={`bg-white rounded-2xl shadow-xl ${sizes[size]} w-full min-h-[40vh] max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Content with dynamic scrolling */}
        <div 
          className="flex-1 overflow-y-auto p-6 modal-content-scroll"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;